import {
	Button,
	Checkbox,
	IconButton,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	TextField,
	Tooltip,
	TableRow,
	TableCell,
	InputAdornment,
	Card,
	CardHeader,
} from '@mui/material';
import {
	CheckIcon,
	ClearIcon,
	ContentCutIcon,
	DeleteIcon,
	DownloadIcon,
	DriveFileRenameIcon,
	FolderIcon,
	InsertDriveFileIcon,
	MoreVertIcon,
} from 'components/icons';
import styles from './FileDirRow.module.css';
import React, { useState, useRef, useEffect } from 'react';
import { renameDir, renameFile } from 'api/canshare/canshareApi';
import { useNotificationProvider } from 'hooks';
import { handleFileDownload } from 'libs/utils/file';
import { formatFileSize } from 'utils/formatFileSize';
import useFileDirRow from '../hooks/useFileDirRow';
import useContextMenu from '../hooks/useContextMenu';
import ActionMenu from './ActionMenu';
import { getNiceDateVanilla } from 'libs/utils/date';

const FileDirRow = ({
	FileDirObj,
	changeDirectory,
	handleToggle,
	checked,
	onClickFile,
	activeDoc,
	isDir,
	addFileToCopyMoveClipboard,
	clearCopyMoveClipboard,
	handleDelete,
	isSm = false,
	tileMode = false,
	isAdmin = false,
	isUser = false,
	isRenamingFileDir,
	setIsRenamingFileDir,
}) => {
	const { notifySuccess, notifyError } = useNotificationProvider();
	const {
		handleEdit,
		cancelEdit,
		editValue,
		setEditValue,
		isEditing,
		setIsEditing,
	} = useFileDirRow(FileDirObj, isRenamingFileDir, isDir);
	const { openContextMenu, closeContextMenu, showContextMenu, anchorEl } =
		useContextMenu();

	const handleMove = () => {
		clearCopyMoveClipboard();
		addFileToCopyMoveClipboard('move');
	};

	// const [clickTimeout, setClickTimeout] = useState(null); // hook to detect single or double click

	const pressTimer = useRef();
	const longPressTriggered = useRef(false); // hook to detect short or long press on phone
	const touchStartPosition = useRef({ x: 0, y: 0 });

	const id = isDir ? FileDirObj.vv_dir_id : FileDirObj.vv_file_id;
	const checkedCondition = isDir
		? checked?.dir?.indexOf(FileDirObj) !== -1
		: checked?.files?.indexOf(FileDirObj) !== -1;
	const icon = isDir ? <FolderIcon /> : <InsertDriveFileIcon />;

	const downloadFile = async (fileId) => {
		if (fileId) {
			const url = `/api/fileuploadservice/Download?FileId=${fileId}`;
			handleFileDownload(url);
		}
	};

	const onTouchStart = (e) => {
		e.stopPropagation();
		e.preventDefault(); // Prevent synthetic click event
		touchStartPosition.current = {
			x: e.touches[0].clientX,
			y: e.touches[0].clientY,
		};

		longPressTriggered.current = false; // Reset the long press flag
		// Start the timer. If the touch is held for 500ms, we consider it a long press
		pressTimer.current = setTimeout(() => {
			longPressTriggered.current = true; // Set the long press flag
			// handleToggle(id, !isDir);
		}, 500);
	};

	const onTouchEnd = (e) => {
		e.stopPropagation();
		e.preventDefault(); // Prevent synthetic click event
		const touchEndPosition = {
			x: e.changedTouches[0].clientX,
			y: e.changedTouches[0].clientY,
		};

		const movedX = Math.abs(
			touchStartPosition.current.x - touchEndPosition.x,
		);
		const movedY = Math.abs(
			touchStartPosition.current.y - touchEndPosition.y,
		);

		// If moved more than 10 pixels in either X or Y direction, we consider it a scroll not a tap.
		if (movedX > 10 || movedY > 10) {
			clearTimeout(pressTimer.current);
			return;
		}

		clearTimeout(pressTimer.current); // Clear the timer. This will prevent the long press flag from being set if the touch ends before 500ms
		if (longPressTriggered.current) {
			// The touch was held for 500ms, so we consider it a long press
			// onLongTouch(e);
			openContextMenu(e);
		} else {
			// The touch was released before 500ms, so we consider it a short press
			handleOpenFileOrDir();
		}
		e.preventDefault();
	};

	const handleSave = async () => {
		if (editValue === FileDirObj.name) {
			setIsEditing(false);
			return;
		}
		if (isDir) {
			const res = await renameDir(FileDirObj.vv_dir_id, editValue);
			if (res.Success) {
				FileDirObj.name = editValue;
				notifySuccess(res.Message || 'Success');
			} else {
				notifyError(res.Message || 'Error');
			}
			// implement save functionality here
			setIsEditing(false);
		} else {
			const res = await renameFile(FileDirObj.vv_file_id, editValue);
			if (res.Success) {
				FileDirObj.name = editValue;
				notifySuccess(res.Message || 'Success');
			} else {
				notifyError(res.Message || 'Error');
			}
			// implement save functionality here
			setIsEditing(false);
		}
	};

	const handleInputChange = (event) => {
		setEditValue(event.target.value);
	};

	const handleOpenFileOrDir = () => {
		if (!isEditing) {
			if (isDir) {
				changeDirectory(FileDirObj);
			} else {
				onClickFile(FileDirObj);
			}
		}
	};

	const clickTimeout = useRef(null);

	const handleClick = () => {
		if (clickTimeout.current === null) {
			// start timer for single click
			clickTimeout.current = setTimeout(() => {
				clickTimeout.current = null;
				// if (name) {
				// 	handleEdit(FileDirObj);
				// }
				handleToggle(FileDirObj, !isDir);
			}, 300); // you may want to adjust the delay
		} else {
			// prevent single click action
			clearTimeout(clickTimeout.current);
			clickTimeout.current = null;
			// handle double click action
			handleOpenFileOrDir();
		}
	};

	useEffect(() => {
		return () => {
			clearTimeout(clickTimeout.current);
		};
	}, []);

	const extraStyles = isDir
		? ''
		: activeDoc?.vv_file_id === id
		? styles.list_item_active
		: '';
	const getActions = (iconSize = 'medium') => {
		const adminOrUserActions = [
			{
				text: 'Rename',
				handler: () => {
					handleEdit(FileDirObj);
				},
				icon: <DriveFileRenameIcon size="small" />,
				disabled: checked?.files.length + checked?.dir.length !== 1,
			},
			{
				text: 'Cut',
				handler: handleMove,
				icon: <ContentCutIcon size="small" />,
				disabled:
					checked?.files.length <= 0 && checked?.dir.length <= 0,
			},
			{
				text: 'Delete',
				handler: () => {
					handleDelete();
				},
				icon: <DeleteIcon size="small" />,
			},
		];
		const viewOnlyActions = [
			{
				text: 'Download',
				handler: () => {
					downloadFile(FileDirObj.vv_file_id);
				},
				icon: <DownloadIcon size="small" />,
				disabled: isDir,
			},
		];
		return isAdmin || isUser
			? [...adminOrUserActions, ...viewOnlyActions]
			: [...viewOnlyActions];
	};
	return (
		<>
			{!tileMode && (
				<TableRow
					onClick={(e) => {
						handleClick();
					}}
					onContextMenu={(e) => {
						e.stopPropagation();
						if (!checkedCondition) {
							handleToggle(FileDirObj, !isDir, true);
						}
						openContextMenu(e);
					}}
					onTouchStart={(e) => {
						onTouchStart(e);
					}}
					onTouchEnd={(e) => {
						onTouchEnd(e);
					}}
					className={`${styles.table_row} ${extraStyles}`}
				>
					<TableCell
						align="left"
						style={{ padding: '0.25rem 0 0.25rem 1rem' }}
					>
						<Tooltip title="Select">
							<Checkbox
								edge="start"
								checked={checkedCondition}
								tabIndex={-1}
								disableRipple
								inputProps={{
									'aria-labelledby': `checkbox-list-label-${id}`,
								}}
								className={styles.check_box}
								onClick={(e) => {
									e.stopPropagation();
									e.preventDefault();
									handleToggle(FileDirObj, !isDir);
								}}
								onTouchStart={(e) => {
									e.stopPropagation();
									e.preventDefault();
								}}
								onTouchEnd={(e) => {
									e.stopPropagation();
									e.preventDefault();
									handleToggle(FileDirObj, !isDir);
								}}
							/>
						</Tooltip>
					</TableCell>
					<TableCell className={`${styles.table_cell}`} align="left">
						<div className={styles.flex_container}>
							<ListItemIcon className={styles.list_icon}>
								{icon}
							</ListItemIcon>
							{!isEditing ? (
								<ListItemText
									primary={FileDirObj.name}
									className={styles.list_item_text}
								/>
							) : (
								<>
									<TextField
										value={editValue}
										onChange={handleInputChange}
										autoFocus
										InputProps={{
											endAdornment: (
												<InputAdornment position="end">
													<Tooltip title="Save">
														<IconButton
															size="small"
															onClick={(e) => {
																e.stopPropagation();
																e.preventDefault();
																setIsRenamingFileDir(
																	undefined,
																);
																handleSave();
															}}
														>
															<CheckIcon />
														</IconButton>
													</Tooltip>
													<Tooltip title="Cancel">
														<IconButton
															size="small"
															onClick={(e) => {
																e.stopPropagation();
																e.preventDefault();
																setIsRenamingFileDir(
																	undefined,
																);
																cancelEdit();
															}}
														>
															<ClearIcon />
														</IconButton>
													</Tooltip>
												</InputAdornment>
											),
										}}
									/>
								</>
							)}
						</div>
					</TableCell>
					{!isSm && (
						<>
							<TableCell
								align="left"
								className={styles.table_cell}
							>
								{FileDirObj.sizeKB === '' ? (
									<hr className={styles.hr} />
								) : (
									// `sizeKB` is in kilobytes; the shared helper takes bytes.
									formatFileSize(Number(FileDirObj.sizeKB) * 1024)
								)}
							</TableCell>
							<TableCell
								align="left"
								className={styles.table_cell}
							>
								{isDir ? 'Folder' : FileDirObj.type}
							</TableCell>
							<TableCell
								align="left"
								className={styles.table_cell}
							>
								{getNiceDateVanilla(
									FileDirObj.date_created,
								) || <hr className={styles.hr} />}
							</TableCell>
						</>
					)}
					<TableCell align="center" className={styles.table_cell}>
						<Tooltip title="More">
							<IconButton
								edge="end"
								onClick={(e) => {
									e.stopPropagation();
									openContextMenu(e);
								}}
								onTouchStart={(e) => {
									e.stopPropagation();
									e.preventDefault();
								}}
								onTouchEnd={(e) => {
									e.stopPropagation();
									openContextMenu(e);
								}}
							>
								<MoreVertIcon />
							</IconButton>
						</Tooltip>
					</TableCell>
				</TableRow>
			)}
			{tileMode && (
				<Card
					className={styles.tile_card}
					onContextMenu={(e) => {
						e.stopPropagation();
						if (!checkedCondition) {
							handleToggle(FileDirObj, !isDir, true);
						}
						openContextMenu(e);
					}}
					onClick={handleClick}
					onTouchStart={onTouchStart}
					onTouchEnd={onTouchEnd}
				>
					<CardHeader
						avatar={
							<div className={styles.card_header_icon}>
								{checkedCondition ? (
									<Tooltip title="Select">
										<Checkbox
											edge="start"
											checked={checkedCondition}
											tabIndex={-1}
											disableRipple
											inputProps={{
												'aria-labelledby': `checkbox-list-label-${id}`,
											}}
											className={styles.check_box}
											onClick={(e) => {
												e.stopPropagation();
												e.preventDefault();
												handleToggle(
													FileDirObj,
													!isDir,
												);
											}}
											onTouchStart={(e) => {
												e.stopPropagation();
												e.preventDefault();
											}}
											onTouchEnd={(e) => {
												e.stopPropagation();
												e.preventDefault();
												handleToggle(
													FileDirObj,
													!isDir,
												);
											}}
										/>
									</Tooltip>
								) : (
									icon
								)}
							</div>
						}
						action={
							<div className={styles.card_header_icon}>
								<Tooltip title="More">
									<IconButton
										onClick={(e) => {
											e.stopPropagation();
											e.preventDefault();
											openContextMenu(e);
										}}
										onTouchStart={(e) => {
											e.stopPropagation();
											e.preventDefault();
										}}
										onTouchEnd={(e) => {
											e.stopPropagation();
											e.preventDefault();
											openContextMenu(e);
										}}
									>
										<MoreVertIcon />
									</IconButton>
								</Tooltip>
							</div>
						}
						title={
							<div className={styles.card_header_title}>
								{isEditing ? (
									<>
										<TextField
											value={editValue}
											onChange={handleInputChange}
											autoFocus
											InputProps={{
												endAdornment: (
													<InputAdornment position="end">
														<Tooltip title="Save">
															<IconButton
																size="small"
																onClick={(
																	e,
																) => {
																	e.stopPropagation();
																	e.preventDefault();
																	setIsRenamingFileDir(
																		undefined,
																	);
																	handleSave();
																}}
															>
																<CheckIcon />
															</IconButton>
														</Tooltip>
														<Tooltip title="Cancel">
															<IconButton
																size="small"
																onClick={(
																	e,
																) => {
																	e.stopPropagation();
																	e.preventDefault();
																	setIsRenamingFileDir(
																		undefined,
																	);
																	cancelEdit();
																}}
															>
																<ClearIcon />
															</IconButton>
														</Tooltip>
													</InputAdornment>
												),
											}}
										/>
									</>
								) : (
									<span>{FileDirObj.name}</span>
								)}
							</div>
						}
						subheader={
							<div className={styles.card_header_subheader}>
								{getNiceDateVanilla(FileDirObj.date_created)}
							</div>
						}
						className={styles.card_header}
					/>
				</Card>
			)}
			{showContextMenu && (
				<ActionMenu
					open={showContextMenu}
					handleClose={closeContextMenu}
					anchorEl={anchorEl}
					actions={getActions('small')}
				/>
			)}
		</>
	);
};

export default FileDirRow;
